import { BigNumber, utils } from "ethers";
import { CONTRACT_ERROR_SIGNATURES } from "./contractErrorSignatures";

/**
 * Reverts a market order meets, shared by the revert decoder and the gasless
 * relayer formatter so both say the same thing for the same failure.
 *
 * `InsufficientLiquidity` is what a perp order gets when a fill would land past
 * its `acceptablePrice`: the bound is per fill and the order is all-or-nothing
 * (docs/TRADING-INPUT-STANDARD.md §5a). Worded as "within your max slippage"
 * because that is the usual cause; "the book is empty" would send traders
 * looking for a problem that is not there.
 */
export const MARKET_ORDER_MESSAGES = {
  insufficientLiquidity:
    "Not enough liquidity within your max slippage to fill this order — the price moved or the book is thin. Nothing was traded. Try a smaller size or raise max slippage.",
  noBookLiquidity:
    "Not enough liquidity on the book for this size. Nothing was traded. Try a smaller size.",
  slippageTooHigh:
    "The price moved past your max slippage before the order filled. Nothing was traded — try again or raise max slippage.",
  maxQuoteInExceeded:
    "This buy would cost more than your max slippage allows. Nothing was traded — try again or raise max slippage.",
  orderWouldCross:
    "This limit price would fill past the order's acceptable price. Move the limit price closer to the market, or use a market order.",
  orderSizeTooLarge: "Order size is above the market maximum. Reduce the size.",
  // `HookRejectedWithdrawal()` takes NO arguments: it says a hook refused, not
  // why. This text used to assert one cause and one remedy anyway -- "reduce a
  // perp position or deposit more" -- and was observed giving advice that could
  // not work. On Arc the risk engine refuses every lock, from 1 unit to the
  // whole balance, because it cannot price one of the account's perp markets;
  // no deposit of any size changes that. Say what is known and no more.
  collateralBackingPerps:
    "The risk engine refused to lock collateral for this order. Spot balances and perp margin share one vault, so an open perp position can block it — but the engine did not say which. Check your open positions, or try again shortly.",
  // When the engine DID say which market it could not price, the trader gets
  // this instead, with the market named.
  riskEngineCannotPriceMarket:
    "The risk engine cannot currently price {{market}}, so it is refusing all collateral operations for this account. This is a venue-side pricing issue, not an amount you can adjust — closing that position, or waiting for pricing to recover, is what unblocks it.",
} as const;

export const KNOWN_REVERT_SELECTORS: Record<string, string> = {
  "0x9e5c05f6": "Position has accrued funding credits exceeding locked collateral. Please contact support.",
  "0xbb55fd27": MARKET_ORDER_MESSAGES.insufficientLiquidity,
  "0x00e4b96e": MARKET_ORDER_MESSAGES.noBookLiquidity,
  "0x76baadda": MARKET_ORDER_MESSAGES.slippageTooHigh,
  "0xa5126dd8": MARKET_ORDER_MESSAGES.maxQuoteInExceeded,
  "0xc08c0ba4": MARKET_ORDER_MESSAGES.orderWouldCross,
  "0x016c4132": MARKET_ORDER_MESSAGES.orderSizeTooLarge,
  "0x871d2813": MARKET_ORDER_MESSAGES.collateralBackingPerps,
  "0xb5cfe37f": "Order size doesn't match the market's lot size.",
  "0x67d83905": "Order size below the market minimum.",
  "0x624d9914": "Price is not aligned to the tick size.",
  "0xcf479181": "Insufficient margin balance.",
  "0x7fd13972": "Selected leverage is not supported by the contract.",
  "0xc119b02b": "Position leverage exceeds the market limit.",
  "0x6ec9be11": "Position not found.",
  "0xc6be462a": "Close size exceeds position size.",
  "0x29ffb255": "Borrow concentration limit exceeded.",
  "0x9cb67ba0": "Borrow utilization limit exceeded.",
  "0x03a0e277": "Account does not exist.",
  "0x6c3bf02a":
    "Not enough account equity for this position. Your collateral value (after risk haircuts) is below the required margin. Deposit more collateral or reduce your position size.",
  "0x41c84735":
    "Account is below maintenance margin. Deposit more collateral or reduce your position size.",
  "0x52a422f7": "This market is currently disabled for trading.",
  "0xc79ce165": "Reduce-only order size exceeds your current position.",
  "0x114fb0ed": "This market is reduce-only — you can only close or reduce a position.",
  "0x4e7d797c": "Close the position or withdraw its collateral before doing this.",
  "0x321116dc": "This withdrawal is still time-locked. Try again after the lock period.",
  "0x0f2ca6e7": "Withdrawal isn't ready yet. Please wait for the unlock time.",
  "0xb34f5c6c": "You already have a pending withdrawal request.",
  "0xddeb79ba": "This position is not eligible for liquidation.",
};

export const SELECTOR_RE = /\b(0x[0-9a-fA-F]{8})\b/;
export const ERROR_DATA_RE = /\b0x[0-9a-fA-F]{8}(?:[0-9a-fA-F]{64})+\b/g;
export const CONTRACT_REVERT_DATA_RE =
  /\b0x[0-9a-fA-F]{8}(?:[0-9a-fA-F]{64})+\b|\b0x[0-9a-fA-F]{8}\b/;

export const CONTRACT_ERROR_INTERFACE = new utils.Interface(
  CONTRACT_ERROR_SIGNATURES.map((signature) => `error ${signature}`)
);

export const ERROR_SIGNATURE_BY_SELECTOR = CONTRACT_ERROR_SIGNATURES.reduce<
  Record<string, string>
>((acc, signature) => {
  acc[utils.id(signature).slice(0, 10).toLowerCase()] = signature;
  return acc;
}, {});

// A custom-error revert payload: 4-byte selector + zero or more 32-byte words.
export const UNKNOWN_ERROR_DATA_RE = /^0x[0-9a-fA-F]{8}(?:[0-9a-fA-F]{64})*$/;
// Words whose magnitude is at least 2^128 and whose top 12 bytes are empty are
// far more likely to be an address than a plain counter/leverage/amount value.
export const ADDRESS_MAGNITUDE = BigNumber.from("0x100000000000000000000000000000000");
